import { useEffect, useMemo, useState } from "react";
import { Code2, Play } from "lucide-react";
import { Modal } from "../../ui/Modal";
import { ScrollShadow } from "../../ui/ScrollShadow";
import { portalReportsAdminApi, type ProcedureDetail, type ProcedureInfo, type TestRunResult } from "../../../api/portalReportsAdminApi";
import {
  COLUMN_FORMATS,
  COLUMN_TOTALS,
  type ReportAudience,
  type ReportChart,
  type ReportColumn,
  type ReportInput,
  type ReportParam,
  type ReportParamSource,
  type ReportSection,
  type ReportSectionKind,
} from "../../../api/portalReportsApi";
import { ChartSection } from "../../reports/dynamic/ChartSection";
import { formatCell, guessFormat } from "../../reports/dynamic/formatCell";
import { ProcedureSourceModal } from "./ProcedureSourceModal";

const inputStyle: React.CSSProperties = { width: "100%", padding: "6px 8px", border: "1px solid var(--portal-border)", borderRadius: 6, fontSize: "0.85rem", fontFamily: "inherit", background: "white" };
const thStyle: React.CSSProperties = { textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--portal-border)", color: "var(--portal-muted)", fontSize: "0.72rem", textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" };
const tdStyle: React.CSSProperties = { padding: "4px 8px", borderBottom: "1px solid var(--portal-border)", verticalAlign: "middle" };

const SOURCE_LABEL: Record<ReportParamSource, string> = {
  input: "User input",
  customerId: "Session customer",
  managerId: "Session manager",
  timeZone: "Session time zone",
  constant: "Constant",
};

/** Default parameter wiring: scope parameters from the session, the rest from inputs named after them. */
function defaultSource(name: string, audience: ReportAudience): ReportParamSource {
  const n = name.toLowerCase();
  if (n === "customerid") return audience === "customer" ? "customerId" : "input";
  if (n === "managerid") return audience === "manager" ? "managerId" : "input";
  if (n === "timezone") return "timeZone";
  return "input";
}

/** Input type for a SQL parameter type. */
export function inputTypeFor(sqlType: string): ReportInput["type"] {
  const t = (sqlType ?? "").toLowerCase();
  if (t === "bit") return "bool";
  if (/^(tinyint|smallint|int|bigint|decimal|numeric|money|smallmoney|float|real)$/.test(t)) return "number";
  if (t === "date") return "date";
  if (/^(datetime|datetime2|smalldatetime|datetimeoffset)$/.test(t)) return "datetime";
  return "text";
}

export function newSectionId(existing: ReportSection[]): string {
  let n = existing.length + 1;
  while (existing.some((s) => s.id === `s${n}`)) n += 1;
  return `s${n}`;
}

/**
 * Modal that configures one section: procedure, parameter wiring, a test
 * run (which also reveals the columns when SQL Server cannot describe the
 * procedure), column display settings, filters and chart settings.
 * Returns the section plus any new inputs the wiring needs.
 */
export function SectionEditor({
  open,
  section,
  inputs,
  audience,
  existingIds,
  onSave,
  onClose,
}: {
  open: boolean;
  section: ReportSection | null;
  inputs: ReportInput[];
  audience: ReportAudience;
  existingIds: string[];
  onSave: (section: ReportSection, newInputs: ReportInput[]) => void;
  onClose: () => void;
}) {
  const [procedures, setProcedures] = useState<ProcedureInfo[]>([]);
  const [procSearch, setProcSearch] = useState("");
  const [detail, setDetail] = useState<ProcedureDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [title, setTitle] = useState("");
  const [kind, setKind] = useState<ReportSectionKind>("table");
  const [procedure, setProcedure] = useState("");
  const [params, setParams] = useState<ReportParam[]>([]);
  const [columns, setColumns] = useState<ReportColumn[]>([]);
  const [filterColumns, setFilterColumns] = useState<string[]>([]);
  const [chart, setChart] = useState<ReportChart>({ kind: "bar", aggregate: "sum", sort: "none", stacked: true });
  const [pageSize, setPageSize] = useState<number>(100);

  const [testValues, setTestValues] = useState<Record<string, string>>({});
  const [testCustomerId, setTestCustomerId] = useState("");
  const [testManagerId, setTestManagerId] = useState("");
  const [testResult, setTestResult] = useState<TestRunResult | null>(null);
  const [testing, setTesting] = useState(false);
  const [sourceOpen, setSourceOpen] = useState(false);

  // Reset from the section being edited whenever the modal opens.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setTestResult(null);
    setDetail(null);
    setProcSearch("");
    setTitle(section?.title ?? "");
    setKind(section?.kind ?? "table");
    setProcedure(section?.procedure ?? "");
    setParams(section?.params ?? []);
    setColumns(section?.columns ?? []);
    setFilterColumns(section?.filterColumns ?? []);
    setChart(section?.chart ?? { kind: "bar", aggregate: "sum", sort: "none", stacked: true });
    setPageSize(section?.pageSize ?? 100);
    setTestValues({});
    portalReportsAdminApi.procedures().then(setProcedures).catch(() => setProcedures([]));
    if (section?.procedure) void loadDetail(section.procedure, section);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  async function loadDetail(name: string, current: ReportSection | null) {
    setLoadingDetail(true);
    setError(null);
    try {
      const d = await portalReportsAdminApi.procedure(name);
      setDetail(d);
      // Parameters: keep existing wiring, add new ones with sensible defaults.
      const existing = current?.procedure === name ? current.params ?? [] : [];
      setParams(
        d.parameters
          .filter((p) => !p.isOutput)
          .map((p) => {
            const prev = existing.find((e) => e.name.toLowerCase() === p.name.toLowerCase());
            return prev ? { ...prev, sqlType: p.sqlType } : { name: p.name, sqlType: p.sqlType, source: defaultSource(p.name, audience), inputKey: p.name };
          }),
      );
      // Columns: from the catalog when it can describe them; the test run refines/creates them.
      if (d.columns.length > 0) mergeColumns(d.columns.map((c) => ({ name: c.name, sqlType: c.sqlType })), current?.procedure === name ? current.columns : []);
      else if (current?.procedure !== name) setColumns([]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load the procedure");
    } finally {
      setLoadingDetail(false);
    }
  }

  function mergeColumns(observed: { name: string; sqlType?: string | null }[], keep: ReportColumn[]) {
    setColumns(
      observed.map((c) => {
        const prev = keep.find((k) => k.name === c.name);
        return prev ? { ...prev, sqlType: c.sqlType ?? prev.sqlType } : { name: c.name, label: c.name, sqlType: c.sqlType ?? undefined, visible: true, format: guessFormat(c.sqlType, c.name), total: "none" };
      }),
    );
  }

  const chooseProcedure = (name: string) => {
    setProcedure(name);
    setTestResult(null);
    if (name) void loadDetail(name, section?.procedure === name ? section : null);
    else {
      setDetail(null);
      setParams([]);
    }
  };

  const runTest = async () => {
    if (!procedure) return;
    setTesting(true);
    setError(null);
    try {
      const values: Record<string, string> = {};
      for (const p of params) {
        if (p.source === "constant") values[p.name] = p.constant ?? "";
        else if (p.source === "input") values[p.name] = testValues[p.name] ?? "";
      }
      const res = await portalReportsAdminApi.testRun(procedure, {
        values,
        customerId: testCustomerId ? Number(testCustomerId) : null,
        managerId: testManagerId ? Number(testManagerId) : null,
      });
      setTestResult(res);
      if (!res.error && res.columns.length > 0) mergeColumns(res.columns, columns);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Test run failed");
    } finally {
      setTesting(false);
    }
  };

  const filteredProcedures = useMemo(() => {
    const q = procSearch.trim().toLowerCase();
    return procedures.filter((p) => !q || p.name.toLowerCase().includes(q));
  }, [procedures, procSearch]);

  const updateParam = (name: string, patch: Partial<ReportParam>) => setParams((ps) => ps.map((p) => (p.name === name ? { ...p, ...patch } : p)));
  const updateColumn = (name: string, patch: Partial<ReportColumn>) => setColumns((cs) => cs.map((c) => (c.name === name ? { ...c, ...patch } : c)));
  const moveColumn = (name: string, dir: -1 | 1) =>
    setColumns((cs) => {
      const i = cs.findIndex((c) => c.name === name);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= cs.length) return cs;
      const next = [...cs];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const testColIndex = useMemo(() => {
    const idx: Record<string, number> = {};
    (testResult?.columns ?? []).forEach((c, i) => {
      idx[c.name] = i;
    });
    return idx;
  }, [testResult]);

  const save = () => {
    if (!procedure) return setError("Choose a procedure.");
    if (columns.length === 0) return setError("Run a test so the columns are known.");
    if (kind === "chart" && (!chart.categoryColumn || (!chart.valueColumn && !(chart.yColumns?.length)))) return setError("A chart needs a category column and a value column.");
    const id = section?.id ?? newSectionId(existingIds.map((i) => ({ id: i }) as ReportSection));
    // Inputs the wiring refers to but the report doesn't have yet.
    const newInputs: ReportInput[] = [];
    for (const p of params) {
      if (p.source !== "input") continue;
      const key = (p.inputKey || p.name).trim();
      if (!key) continue;
      if (!inputs.some((i) => i.key.toLowerCase() === key.toLowerCase()) && !newInputs.some((i) => i.key.toLowerCase() === key.toLowerCase())) {
        const t = inputTypeFor(p.sqlType ?? "");
        newInputs.push({ key, label: key, type: t, required: false, localToUtc: t === "date" || t === "datetime" });
      }
    }
    onSave(
      {
        id,
        title: title.trim() || procedure.replace(/^spPortalReport_/i, ""),
        kind,
        procedure,
        params: params.map((p) => ({ ...p, inputKey: p.source === "input" ? (p.inputKey || p.name).trim() : undefined, constant: p.source === "constant" ? p.constant : undefined })),
        columns,
        filterColumns: filterColumns.filter((f) => columns.some((c) => c.name === f)),
        chart: kind === "chart" ? chart : undefined,
        pageSize: kind === "table" ? pageSize : undefined,
      },
      newInputs,
    );
  };

  const paramInputs = params.filter((p) => p.source === "input");

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={section ? `Section: ${section.title || section.id}` : "New section"}
      maxWidth={1100}
      busy={testing}
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={save}>Save section</button>
        </>
      }
    >
      <div style={{ display: "grid", gap: 14 }}>
        {error && <div className="alert alert-error">{error}</div>}

        <Block title="1. Section">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
            <Field label="Title">
              <input className="portal-input-inline" style={inputStyle} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Shown above the section" />
            </Field>
            <Field label="Kind">
              <select className="portal-input-inline" style={inputStyle} value={kind} onChange={(e) => setKind(e.target.value as ReportSectionKind)}>
                <option value="table">Table</option>
                <option value="stats">Stat tiles</option>
                <option value="chart">Chart</option>
              </select>
            </Field>
            {kind === "table" && (
              <Field label="Rows per page">
                <input className="portal-input-inline" style={inputStyle} type="number" min={10} max={1000} value={pageSize} onChange={(e) => setPageSize(Math.max(10, Number(e.target.value) || 100))} />
              </Field>
            )}
          </div>
        </Block>

        <Block title="2. Stored procedure">
          <div style={{ display: "grid", gridTemplateColumns: "minmax(200px, 1fr) minmax(240px, 2fr) auto", gap: 10, alignItems: "end" }}>
            <Field label="Filter">
              <input className="portal-input-inline" style={inputStyle} value={procSearch} onChange={(e) => setProcSearch(e.target.value)} placeholder="Type to filter…" />
            </Field>
            <Field label="Procedure (dbo.spPortalReport_*)">
              <select className="portal-input-inline" style={inputStyle} value={procedure} onChange={(e) => chooseProcedure(e.target.value)}>
                <option value="">Select a procedure…</option>
                {procedure && !filteredProcedures.some((p) => p.name === procedure) && <option value={procedure}>{procedure}</option>}
                {filteredProcedures.map((p) => (
                  <option key={p.name} value={p.name}>{p.name}</option>
                ))}
              </select>
            </Field>
            <button type="button" className="btn btn-secondary" onClick={() => setSourceOpen(true)} disabled={!procedure} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Code2 size={14} /> View source
            </button>
          </div>
          {loadingDetail && <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>Loading procedure…</div>}
          {procedures.length === 0 && <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>No procedures found with the report prefix. The team creates them via SQL scripts.</div>}
        </Block>

        {procedure && (
          <Block title="3. Parameters">
            {params.length === 0 ? (
              <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>This procedure has no parameters.</div>
            ) : (
              <ScrollShadow>
                <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.85rem" }}>
                  <thead>
                    <tr>
                      <th style={thStyle}>Parameter</th>
                      <th style={thStyle}>SQL type</th>
                      <th style={thStyle}>Source</th>
                      <th style={{ ...thStyle, minWidth: 200 }}>Input key / constant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {params.map((p) => {
                      const meta = detail?.parameters.find((d) => d.name === p.name);
                      return (
                        <tr key={p.name}>
                          <td style={{ ...tdStyle, fontFamily: "monospace" }}>@{p.name}{meta?.hasDefault ? <span style={{ color: "var(--portal-muted)" }}> (optional)</span> : null}</td>
                          <td style={tdStyle}>{p.sqlType}</td>
                          <td style={tdStyle}>
                            <select className="portal-input-inline" style={inputStyle} value={p.source} onChange={(e) => updateParam(p.name, { source: e.target.value as ReportParamSource })}>
                              {(Object.keys(SOURCE_LABEL) as ReportParamSource[]).map((s) => (
                                <option key={s} value={s}>{SOURCE_LABEL[s]}</option>
                              ))}
                            </select>
                          </td>
                          <td style={tdStyle}>
                            {p.source === "input" && (
                              <input className="portal-input-inline" style={inputStyle} value={p.inputKey ?? p.name} onChange={(e) => updateParam(p.name, { inputKey: e.target.value })} placeholder="Input key (shared across sections)" />
                            )}
                            {p.source === "constant" && (
                              <input className="portal-input-inline" style={inputStyle} value={p.constant ?? ""} onChange={(e) => updateParam(p.name, { constant: e.target.value })} placeholder="Value" />
                            )}
                            {(p.source === "customerId" || p.source === "managerId" || p.source === "timeZone") && (
                              <span style={{ color: "var(--portal-muted)" }}>from the signed-in session</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </ScrollShadow>
            )}
          </Block>
        )}

        {procedure && (
          <Block title="4. Test run">
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
              {(audience === "customer" || params.some((p) => p.source === "customerId")) && (
                <Field label="Run as customer id">
                  <input className="portal-input-inline" style={{ ...inputStyle, width: 120 }} value={testCustomerId} onChange={(e) => setTestCustomerId(e.target.value)} placeholder="e.g. 611" />
                </Field>
              )}
              {(audience === "manager" || params.some((p) => p.source === "managerId")) && (
                <Field label="Run as manager id">
                  <input className="portal-input-inline" style={{ ...inputStyle, width: 120 }} value={testManagerId} onChange={(e) => setTestManagerId(e.target.value)} placeholder="e.g. 21" />
                </Field>
              )}
              {paramInputs.map((p) => (
                <Field key={p.name} label={`@${p.name}`}>
                  <input className="portal-input-inline" style={{ ...inputStyle, width: 160 }} value={testValues[p.name] ?? ""} onChange={(e) => setTestValues((v) => ({ ...v, [p.name]: e.target.value }))} placeholder={p.sqlType} />
                </Field>
              ))}
              <button type="button" className="btn btn-primary" onClick={() => void runTest()} disabled={testing} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Play size={14} /> {testing ? "Running…" : "Run test"}
              </button>
            </div>
            <div style={{ fontSize: "0.8rem", color: "var(--portal-muted)" }}>
              Dates as yyyy-MM-dd or yyyy-MM-ddTHH:mm (interpreted in the customer's time zone when one is chosen). The test also fills the column list.
            </div>
            {testResult?.error && <div className="alert alert-error" style={{ fontFamily: "monospace", whiteSpace: "pre-wrap" }}>{testResult.error}</div>}
            {testResult && !testResult.error && (
              <>
                <div style={{ fontSize: "0.85rem", color: "var(--portal-muted)" }}>
                  {testResult.rows.length} {testResult.rows.length === 1 ? "row" : "rows"}{testResult.truncated ? " (first rows only)" : ""} · {testResult.columns.length} columns
                </div>
                <ScrollShadow style={{ border: "1px solid var(--portal-border)", borderRadius: 8 }} scrollStyle={{ maxHeight: 220, overflowY: "auto" }}>
                  <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.8rem" }}>
                    <thead>
                      <tr>{testResult.columns.map((c) => <th key={c.name} style={thStyle}>{c.name}</th>)}</tr>
                    </thead>
                    <tbody>
                      {testResult.rows.slice(0, 20).map((r, i) => (
                        <tr key={i}>{r.map((v, j) => <td key={j} style={{ ...tdStyle, whiteSpace: "nowrap" }}>{formatCell(v, columns.find((c) => c.name === testResult.columns[j]?.name))}</td>)}</tr>
                      ))}
                    </tbody>
                  </table>
                </ScrollShadow>
              </>
            )}
          </Block>
        )}

        {columns.length > 0 && (
          <Block title="5. Columns">
            <ScrollShadow>
              <table style={{ borderCollapse: "collapse", width: "100%", fontSize: "0.85rem" }}>
                <thead>
                  <tr>
                    <th style={thStyle}>Show</th>
                    <th style={thStyle}>Column</th>
                    <th style={{ ...thStyle, minWidth: 160 }}>Label</th>
                    <th style={thStyle}>Format</th>
                    <th style={thStyle}>Decimals</th>
                    <th style={thStyle}>Align</th>
                    <th style={thStyle}>Total</th>
                    <th style={thStyle} title="Datetime values are already local (no time zone shift)">Local time</th>
                    <th style={thStyle}>Filter</th>
                    <th style={thStyle} aria-label="Order" />
                  </tr>
                </thead>
                <tbody>
                  {columns.map((c, i) => (
                    <tr key={c.name}>
                      <td style={tdStyle}><input type="checkbox" checked={c.visible !== false} onChange={(e) => updateColumn(c.name, { visible: e.target.checked })} /></td>
                      <td style={{ ...tdStyle, fontFamily: "monospace", whiteSpace: "nowrap" }}>{c.name}<div style={{ color: "var(--portal-muted)", fontSize: "0.72rem" }}>{c.sqlType}</div></td>
                      <td style={tdStyle}><input className="portal-input-inline" style={inputStyle} value={c.label ?? ""} onChange={(e) => updateColumn(c.name, { label: e.target.value })} /></td>
                      <td style={tdStyle}>
                        <select className="portal-input-inline" style={inputStyle} value={c.format ?? "text"} onChange={(e) => updateColumn(c.name, { format: e.target.value as ReportColumn["format"] })}>
                          {COLUMN_FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                        </select>
                      </td>
                      <td style={tdStyle}><input className="portal-input-inline" style={{ ...inputStyle, width: 60 }} type="number" min={0} max={6} value={c.decimals ?? ""} onChange={(e) => updateColumn(c.name, { decimals: e.target.value === "" ? undefined : Number(e.target.value) })} /></td>
                      <td style={tdStyle}>
                        <select className="portal-input-inline" style={inputStyle} value={c.align ?? ""} onChange={(e) => updateColumn(c.name, { align: (e.target.value || undefined) as ReportColumn["align"] })}>
                          <option value="">Auto</option>
                          <option value="left">Left</option>
                          <option value="right">Right</option>
                          <option value="center">Center</option>
                        </select>
                      </td>
                      <td style={tdStyle}>
                        <select className="portal-input-inline" style={inputStyle} value={c.total ?? "none"} onChange={(e) => updateColumn(c.name, { total: e.target.value as ReportColumn["total"] })}>
                          {COLUMN_TOTALS.map((t) => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      <td style={{ ...tdStyle, textAlign: "center" }}><input type="checkbox" checked={!!c.isLocalTime} onChange={(e) => updateColumn(c.name, { isLocalTime: e.target.checked })} /></td>
                      <td style={{ ...tdStyle, textAlign: "center" }}>
                        <input type="checkbox" checked={filterColumns.includes(c.name)} onChange={(e) => setFilterColumns((f) => (e.target.checked ? [...f, c.name] : f.filter((x) => x !== c.name)))} />
                      </td>
                      <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                        <button type="button" className="btn btn-secondary" onClick={() => moveColumn(c.name, -1)} disabled={i === 0} style={{ padding: "2px 8px" }} aria-label="Move up">↑</button>{" "}
                        <button type="button" className="btn btn-secondary" onClick={() => moveColumn(c.name, 1)} disabled={i === columns.length - 1} style={{ padding: "2px 8px" }} aria-label="Move down">↓</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </ScrollShadow>
            <div style={{ fontSize: "0.8rem", color: "var(--portal-muted)" }}>"Filter" offers the column's values as chips above the section (applied in the browser).</div>
          </Block>
        )}

        {kind === "chart" && columns.length > 0 && (
          <Block title="6. Chart">
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
              <Field label="Chart type">
                <select className="portal-input-inline" style={inputStyle} value={chart.kind} onChange={(e) => setChart({ ...chart, kind: e.target.value as ReportChart["kind"] })}>
                  <option value="bar">Bar</option>
                  <option value="line">Line</option>
                  <option value="pie">Pie</option>
                </select>
              </Field>
              <Field label="Category (x)">
                <ColumnSelect columns={columns} value={chart.categoryColumn} onChange={(v) => setChart({ ...chart, categoryColumn: v })} />
              </Field>
              <Field label="Series (pivot, optional)">
                <ColumnSelect columns={columns} value={chart.seriesColumn} onChange={(v) => setChart({ ...chart, seriesColumn: v })} allowNone />
              </Field>
              <Field label="Value">
                <ColumnSelect columns={columns} value={chart.valueColumn} onChange={(v) => setChart({ ...chart, valueColumn: v })} allowNone noneLabel="Count rows" />
              </Field>
              <Field label="Aggregate">
                <select className="portal-input-inline" style={inputStyle} value={chart.aggregate ?? "sum"} onChange={(e) => setChart({ ...chart, aggregate: e.target.value as ReportChart["aggregate"] })}>
                  {["sum", "count", "avg", "min", "max"].map((a) => <option key={a} value={a}>{a}</option>)}
                </select>
              </Field>
              <Field label="Sort">
                <select className="portal-input-inline" style={inputStyle} value={chart.sort ?? "none"} onChange={(e) => setChart({ ...chart, sort: e.target.value as ReportChart["sort"] })}>
                  <option value="none">Result order</option>
                  <option value="value">By value (desc)</option>
                  <option value="category">By category</option>
                </select>
              </Field>
              <Field label="Top N (rest → Other)">
                <input className="portal-input-inline" style={inputStyle} type="number" min={0} value={chart.topN ?? ""} onChange={(e) => setChart({ ...chart, topN: e.target.value === "" ? undefined : Number(e.target.value) })} placeholder="all" />
              </Field>
              <Field label="Y axis at least">
                <input className="portal-input-inline" style={inputStyle} type="number" min={0} value={chart.yMax ?? ""} onChange={(e) => setChart({ ...chart, yMax: e.target.value === "" ? undefined : Number(e.target.value) })} placeholder="auto" />
              </Field>
              <Field label="X range from (numeric categories)">
                <input className="portal-input-inline" style={inputStyle} type="number" value={chart.categoryFrom ?? ""} onChange={(e) => setChart({ ...chart, categoryFrom: e.target.value === "" ? undefined : Number(e.target.value) })} placeholder="data" />
              </Field>
              <Field label="X range to at least">
                <input className="portal-input-inline" style={inputStyle} type="number" value={chart.categoryTo ?? ""} onChange={(e) => setChart({ ...chart, categoryTo: e.target.value === "" ? undefined : Number(e.target.value) })} placeholder="data" />
              </Field>
              <Field label="Value format">
                <select className="portal-input-inline" style={inputStyle} value={chart.valueFormat ?? ""} onChange={(e) => setChart({ ...chart, valueFormat: (e.target.value || undefined) as ReportChart["valueFormat"] })}>
                  <option value="">Like the value column</option>
                  {COLUMN_FORMATS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                </select>
              </Field>
            </div>
            <div style={{ display: "flex", gap: 16, fontSize: "0.85rem" }}>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><input type="checkbox" checked={!!chart.stacked} onChange={(e) => setChart({ ...chart, stacked: e.target.checked })} /> Stacked</label>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><input type="checkbox" checked={!!chart.horizontal} onChange={(e) => setChart({ ...chart, horizontal: e.target.checked })} /> Horizontal bars</label>
            </div>
            {testResult && !testResult.error && chart.categoryColumn && (
              <div style={{ border: "1px solid var(--portal-border)", borderRadius: 8, padding: 8 }}>
                <ChartSection chart={chart} columns={columns} colIndex={testColIndex} rows={testResult.rows} />
              </div>
            )}
          </Block>
        )}
      </div>
      <ProcedureSourceModal name={sourceOpen ? procedure : null} parameters={detail?.parameters} onClose={() => setSourceOpen(false)} />
    </Modal>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gap: 8, padding: 12, border: "1px solid var(--portal-border)", borderRadius: 8 }}>
      <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{title}</div>
      {children}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "grid", gap: 4, fontSize: "0.85rem" }}>
      <span style={{ color: "var(--portal-muted)" }}>{label}</span>
      {children}
    </label>
  );
}

function ColumnSelect({ columns, value, onChange, allowNone, noneLabel = "—" }: { columns: ReportColumn[]; value: string | undefined; onChange: (v: string | undefined) => void; allowNone?: boolean; noneLabel?: string }) {
  return (
    <select className="portal-input-inline" style={inputStyle} value={value ?? ""} onChange={(e) => onChange(e.target.value || undefined)}>
      {(allowNone || !value) && <option value="">{allowNone ? noneLabel : "Select…"}</option>}
      {columns.map((c) => <option key={c.name} value={c.name}>{c.label && c.label !== c.name ? `${c.label} (${c.name})` : c.name}</option>)}
    </select>
  );
}
